import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ModeContext, useConfig } from '../../../context';
import { Mode } from '../../../types';
import { getOrder } from '../../../Checkout/store/order/selectors';
import { getCheckinOrder } from '../../../Modules/Checkin/store/order/selectors';
import { useInsuranceState } from '../../store';
import { InsuranceProductState, ProgramCode } from '../../store/types';
import { Props } from './InsurancePage';
import * as React from 'react';
import { scrollTo } from '../../../utils';
import WebskyAnalyticsApollo from '../../../DataLayerAnalytics/WebskyAnalyticsApollo';
import { WxEvents } from '../../../DataLayerAnalytics/types';
import { analytics_addInsurance, analytics_removeInsurance } from '../../../Insurance/utils';
import { useImportantInsurances } from '../../hooks';

export const useInsurancePage = ({
	availablePrograms,
	onTotalInsurancePriceChange,
	onSaveOrderServicesParamsChange,
	onServicesChanged
}: Pick<
	Props,
	'availablePrograms' | 'onTotalInsurancePriceChange' | 'onSaveOrderServicesParamsChange' | 'onServicesChanged'
>) => {
	const insurancePriorities = useConfig().global.insurance.insurancePriorities;

	const mode = useContext(ModeContext);
	const order = mode === Mode.Booking ? useSelector(getOrder) : useSelector(getCheckinOrder);

	React.useEffect(() => {
		scrollTo(0);

		if (orderWithAvailableInsurances.additionalServices.insurances?.insurancePrograms?.length) {
			WebskyAnalyticsApollo.dispatchWxViewEvent(WxEvents.InsurancesList, {
				servicesIds: orderWithAvailableInsurances.additionalServices.insurances.insurancePrograms.map(
					insurance => insurance.code
				)
			});
		}
	}, []);

	const orderWithAvailableInsurances = useMemo(() => {
		if (!availablePrograms?.length) {
			return order;
		}

		const selectedInsurances =
			order.additionalServices.insurances?.selectedInsurances?.filter(insurance =>
				availablePrograms.some(
					availableInsurance => availableInsurance.code === insurance.insuranceProgram.code
				)
			) ?? [];

		return {
			...order,
			additionalServices: {
				...order.additionalServices,
				insurances: {
					...order.additionalServices.insurances,
					insurancePrograms: availablePrograms,
					selectedInsurances
				}
			}
		};
	}, [order, availablePrograms]);

	const {
		state,
		initialStateChanged,
		dispatch,
		selectedProgramsCodes,
		insurancesWithExcludedMainPrograms,
		mainPrograms,
		availableInsurances,
		selectedInsurances,
		saveOrderServicesParams,
		totalPrice
	} = useInsuranceState(orderWithAvailableInsurances);

	const importantInsurances = useImportantInsurances();

	const doNotRenderForAllPassengersText = useMemo(() => {
		return order.travellers.length === 1;
	}, [order.travellers]);

	const prioritizedInsurances = useMemo(
		() =>
			insurancePriorities.length > 0
				? insurancesWithExcludedMainPrograms.sort((first, second) => {
						if (insurancePriorities.indexOf(first.code) === -1) {
							return 1;
						}

						if (insurancePriorities.indexOf(second.code) === -1) {
							return -1;
						}

						return insurancePriorities.indexOf(first.code) - insurancePriorities.indexOf(second.code);
				  })
				: insurancesWithExcludedMainPrograms,
		[insurancePriorities, insurancesWithExcludedMainPrograms]
	);

	const toggle = (code: ProgramCode) => {
		const program = state.availableInsurances[code];
		const isSelected = Boolean(state.selectedInsurances[code]);

		if (isSelected) {
			analytics_removeInsurance(program, order.travellers);
		} else {
			analytics_addInsurance(program, order.travellers);
		}

		dispatch({
			type: isSelected ? 'removeInsurance' : 'selectInsurance',
			payload: program
		});
	};

	const select = (code: ProgramCode) => {
		const program = state.availableInsurances[code];

		importantInsurances.onTouchInsurance(code);

		analytics_addInsurance(program, order.travellers);

		dispatch({
			type: 'selectInsurance',
			payload: program
		});
	};

	const remove = (code: ProgramCode) => {
		const program = state.availableInsurances[code];

		importantInsurances.onTouchInsurance(code);

		if (importantInsurances.getIsTouched(code)) {
			analytics_removeInsurance(program, order.travellers);
		}

		dispatch({
			type: 'removeInsurance',
			payload: program
		});
	};

	const getProductState = useCallback(
		(code: ProgramCode): InsuranceProductState => {
			if (state.confirmedInsurances[code]) {
				return 'confirmed';
			} else if (state.selectedInsurances[code]) {
				return 'selected';
			} else {
				return 'available';
			}
		},
		[state]
	);

	const getPolicyUrl = useCallback(
		(code: ProgramCode) => {
			return state.confirmedInsurances[code]?.policyUrl;
		},
		[state]
	);

	// child-parent
	useEffect(() => {
		onServicesChanged?.(initialStateChanged);
	}, [initialStateChanged, onServicesChanged]);

	useEffect(() => {
		onSaveOrderServicesParamsChange?.(saveOrderServicesParams);
	}, [onSaveOrderServicesParamsChange, saveOrderServicesParams]);

	useEffect(() => {
		onTotalInsurancePriceChange?.(totalPrice);
	}, [onTotalInsurancePriceChange, totalPrice]);

	return {
		selectedProgramsCodes,
		doNotRenderForAllPassengersText,
		insurancesWithExcludedMainPrograms: prioritizedInsurances,
		availableInsurances,
		selectedInsurances,
		getProductState,
		getPolicyUrl,
		toggle,
		select,
		remove,
		mainPrograms,
		isLoading: state.isLoading
	};
};
